/**
 * Automatische Antworten im Wizard (G-15) — rein und testbar.
 *
 * Gleiche Felder wie der alte Builder (`campaign.autoReply`: isActive, goalofCommunication, caseHandling). Die Vorgaben
 * (Ziel + 13 Fälle je Sprache) liefert der Server mit dem Snapshot (`auto-reply-defaults.ts`); solange der Nutzer Ziel oder
 * Fälle nicht bearbeitet hat (`custom`), folgen sie der gewählten Sprache.
 */

import type {
  WizardAutoReply,
  WizardAutoReplyDefaults,
  WizardState,
} from '../../campaign-wizard.types';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Sprache der Vorgaben; „Profilsprache erkennen“ (auto) hat keine eigene Vorgabe und lässt den Text stehen. */
const defaultsLanguage = (language: WizardState['language']): 'de' | 'en' | null =>
  language === 'de' || language === 'en' ? language : null;

/** Stand der automatischen Antworten: der gespeicherte, sonst „aus“ mit den Vorgaben (Altentwurf, noch ohne Snapshot-Fassung). */
export const resolveAutoReply = (
  state: Pick<WizardState, 'autoReply' | 'language'>,
  defaults: WizardAutoReplyDefaults | null
): WizardAutoReply | null => {
  if (state.autoReply) return state.autoReply;
  if (!defaults) return null;
  return { isActive: false, ...defaults[defaultsLanguage(state.language) ?? 'de'], custom: false };
};

export const toggleAutoReply = (current: WizardAutoReply): WizardAutoReply => ({
  ...current,
  isActive: !current.isActive,
});

/** Nutzer bearbeitet Ziel oder Fälle: ab jetzt folgt der Text nicht mehr der Sprache. */
export const editAutoReply = (
  current: WizardAutoReply,
  change: Partial<Pick<WizardAutoReply, 'goalofCommunication' | 'caseHandling'>>
): WizardAutoReply => ({ ...current, ...change, custom: true });

/** Sprachwechsel in Schritt 5: unbearbeitete Vorgaben wechseln mit (Schalter bleibt); bearbeitete bleiben unberührt. */
export const autoReplyLanguagePatch = (
  state: Pick<WizardState, 'autoReply' | 'language'>,
  language: WizardState['language'],
  defaults: WizardAutoReplyDefaults | null
): Partial<WizardState> => {
  const lang = defaultsLanguage(language);
  const current = resolveAutoReply(state, defaults);
  if (!lang || !defaults || !current || current.custom) return {};
  return { autoReply: { ...current, ...defaults[lang], custom: false } };
};

/** Zeile der Zusammenfassung (Schritt 7). */
export const autoReplySummary = (autoReply: WizardAutoReply | undefined, t: Translate): string =>
  autoReply?.isActive
    ? t('wizRevAutoReplyOn', { count: autoReply.caseHandling.length })
    : t('wizRevAutoReplyOff');
